import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { baseQuery, endpoints } from 'utils/api/endpoints';
import {
  State,
  EmailProvidersResponse,
  EmailLogsResponse,
  EmailStatsResponse,
  EmailProvider,
} from './types';

// Transform API data to frontend format
const transformEmailProviderData = (apiData: any): EmailProvider[] => {
  if (!apiData) return [];

  return apiData.map((item: any) => ({
    id: item.id?.toString() || item.provider_id?.toString(),
    name: item.name || item.provider_name,
    type: item.type || item.provider_type,
    isActive: item.is_active || item.status === 1 || item.status === true,
    config: {
      apiKey: item.api_key || item.config?.api_key,
      senderName: item.sender_name || item.config?.sender_name,
      senderEmail: item.sender_email || item.config?.sender_email,
      domain: item.domain || item.config?.domain,
      region: item.region || item.config?.region,
      webhookUrl: item.webhook_url || item.config?.webhook_url,
      webhookSecret: item.webhook_secret || item.config?.webhook_secret,
      webhookSigningKey:
        item.webhook_signing_key || item.config?.webhook_signing_key,
    },
    stats: {
      totalSent: item.stats?.total_sent || item.total_sent || 0,
      delivered: item.stats?.delivered || item.delivered || 0,
      failed: item.stats?.failed || item.failed || 0,
      deliveryRate: item.stats?.delivery_rate || item.delivery_rate || 0,
    },
  }));
};

export const initialState: State = {
  selectedProvider: null,
};

const slice = createSlice({
  name: 'emailConfig',
  initialState,
  reducers: {
    setSelectedProvider: (state, action: PayloadAction<any>) => {
      state.selectedProvider = action.payload;
    },
  },
});

export const api = createApi({
  reducerPath: 'emailConfigApi',
  baseQuery: baseQuery,
  tagTypes: [
    'EmailProviders',
    'EmailLogs',
    'EmailStats',
    'BrevoConfig',
    'MailgunConfig',
  ],
  endpoints: builder => ({
    // Get all email providers
    getEmailProviders: builder.query<EmailProvidersResponse, void>({
      query: () => ({
        url: endpoints.EMAIL_PROVIDERS,
        method: 'GET',
      }),
      providesTags: ['EmailProviders'],
    }),

    // Brevo specific endpoints
    getBrevoConfig: builder.query<any, void>({
      query: () => ({
        url: `${endpoints.EMAIL_SERVICE}/brevo/config`,
        method: 'GET',
      }),
      providesTags: ['BrevoConfig'],
    }),

    addBrevoConfig: builder.mutation<any, any>({
      query: config => {
        const requestBody = {
          sender_name: config.senderName,
          sender_email: config.senderEmail,
          api_key: config.apiKey,
          is_active: config.isActive ? 1 : 0,
        };

        console.log('Adding Brevo config:', requestBody);

        return {
          url: `${endpoints.EMAIL_SERVICE}/brevo/config`,
          method: 'POST',
          body: requestBody,
        };
      },
      invalidatesTags: ['BrevoConfig', 'EmailProviders'],
    }),

    // update brevo config
    updateBrevoConfig: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => {
        const requestBody = {
          sender_name: data.senderName,
          sender_email: data.senderEmail,
          api_key: data.apiKey,
          is_active: data.isActive ? 1 : 0,
        };

        console.log('Updating Brevo config:', id, requestBody);

        return {
          url: `${endpoints.EMAIL_SERVICE}/brevo/config`,
          method: 'POST',
          body: requestBody,
        };
      },
      invalidatesTags: ['BrevoConfig', 'EmailProviders'],
    }),

    deleteBrevoConfig: builder.mutation<any, string>({
      query: id => ({
        url: `${endpoints.EMAIL_SERVICE}/brevo/config/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['BrevoConfig', 'EmailProviders'],
    }),

    testBrevoConfig: builder.mutation<any, { id: string; testData: any }>({
      query: ({ id, testData }) => ({
        url: `${endpoints.EMAIL_SERVICE}/brevo/test/${id}`,
        method: 'POST',
        body: testData,
      }),
    }),

    toggleBrevoStatus: builder.mutation<any, { id: string; isActive: boolean }>(
      {
        query: ({ id, isActive }) => ({
          url: `${endpoints.EMAIL_SERVICE}/brevo/config/${id}/status`,
          method: 'PUT',
          body: { is_active: isActive ? 1 : 0 },
        }),
        invalidatesTags: ['BrevoConfig', 'EmailProviders'],
      },
    ),

    // Mailgun specific endpoints
    getMailgunConfig: builder.query<any, void>({
      query: () => ({
        url: `${endpoints.EMAIL_SERVICE}/mailgun/config`,
        method: 'GET',
      }),
      providesTags: ['MailgunConfig'],
    }),

    addMailgunConfig: builder.mutation<any, any>({
      query: config => {
        const requestBody = {
          sender_name: config.senderName,
          sender_email: config.senderEmail,
          api_key: config.apiKey,
          domain: config.domain,
          webhook_signing_key: config.webhookSigningKey,
          is_active: config.isActive ? 1 : 0,
        };

        console.log('Adding Mailgun config:', requestBody);

        return {
          url: `${endpoints.EMAIL_SERVICE}/mailgun/config`,
          method: 'POST',
          body: requestBody,
        };
      },
      invalidatesTags: ['MailgunConfig', 'EmailProviders'],
    }),

    updateMailgunConfig: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => {
        const requestBody = {
          sender_name: data.senderName,
          sender_email: data.senderEmail,
          api_key: data.apiKey,
          domain: data.domain,
          webhook_signing_key: data.webhookSigningKey,
          is_active: data.isActive ? 1 : 0,
        };

        console.log('Updating Mailgun config:', id, requestBody);

        return {
          url: `${endpoints.EMAIL_SERVICE}/mailgun/config/${id}`,
          method: 'PUT',
          body: requestBody,
        };
      },
      invalidatesTags: ['MailgunConfig', 'EmailProviders'],
    }),

    deleteMailgunConfig: builder.mutation<any, string>({
      query: id => ({
        url: `${endpoints.EMAIL_SERVICE}/mailgun/config/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MailgunConfig', 'EmailProviders'],
    }),

    testMailgunConfig: builder.mutation<any, { id: string; testData: any }>({
      query: ({ id, testData }) => ({
        url: `${endpoints.EMAIL_SERVICE}/mailgun/test/${id}`,
        method: 'POST',
        body: testData,
      }),
    }),

    toggleMailgunStatus: builder.mutation<
      any,
      { id: string; isActive: boolean }
    >({
      query: ({ id, isActive }) => ({
        url: `${endpoints.EMAIL_SERVICE}/mailgun/config/${id}/status`,
        method: 'PUT',
        body: { is_active: isActive ? 1 : 0 },
      }),
      invalidatesTags: ['MailgunConfig', 'EmailProviders'],
    }),

    // Legacy endpoints for backward compatibility
    addEmailProvider: builder.mutation<any, EmailProvider>({
      query: provider => {
        const requestBody = {
          name: provider.name,
          type: provider.type,
          is_active: provider.isActive,
          config: {
            api_key: provider.config?.apiKey || '',
            sender_name: provider.config?.senderName || '',
            sender_email: provider.config?.senderEmail || '',
            domain: provider.config?.domain || '',
            region: provider.config?.region || 'us',
            webhook_url: provider.config?.webhookUrl || '',
            webhook_secret: provider.config?.webhookSecret || '',
            webhook_signing_key: provider.config?.webhookSigningKey || '',
          },
        };

        console.log('Adding provider:', provider.type, requestBody);

        return {
          url: `${endpoints.EMAIL_SERVICE}/${provider.type}/config`,
          method: 'POST',
          body: requestBody,
        };
      },
      invalidatesTags: ['EmailProviders'],
    }),

    updateEmailProvider: builder.mutation<
      any,
      { id: string; data: EmailProvider }
    >({
      query: ({ id, data }) => {
        const requestBody = {
          name: data.name,
          type: data.type,
          is_active: data.isActive,
          config: {
            api_key: data.config?.apiKey || '',
            sender_name: data.config?.senderName || '',
            sender_email: data.config?.senderEmail || '',
            domain: data.config?.domain || '',
            region: data.config?.region || 'us',
            webhook_url: data.config?.webhookUrl || '',
            webhook_secret: data.config?.webhookSecret || '',
            webhook_signing_key: data.config?.webhookSigningKey || '',
          },
        };

        console.log('Updating provider:', data.type, id, requestBody);

        return {
          url: `${endpoints.EMAIL_SERVICE}/${data.type}/config/${id}`,
          method: 'PUT',
          body: requestBody,
        };
      },
      invalidatesTags: ['EmailProviders'],
    }),

    deleteEmailProvider: builder.mutation<any, { id: string; type: string }>({
      query: ({ id, type }) => ({
        url: `${endpoints.EMAIL_SERVICE}/${type}/config/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EmailProviders'],
    }),

    testEmailProvider: builder.mutation<
      any,
      { id: string; type: string; testData: any }
    >({
      query: ({ id, type, testData }) => ({
        url: `${endpoints.EMAIL_SERVICE}/${type}/test/${id}`,
        method: 'POST',
        body: testData,
      }),
    }),

    toggleProviderStatus: builder.mutation<
      any,
      { id: string; type: string; isActive: boolean }
    >({
      query: ({ id, type, isActive }) => ({
        url: `${endpoints.EMAIL_SERVICE}/${type}/config/${id}/status`,
        method: 'PUT',
        body: { is_active: isActive },
      }),
      invalidatesTags: ['EmailProviders'],
    }),

    // Get email logs
    getEmailLogs: builder.query<
      EmailLogsResponse,
      { status?: string; provider?: string; days?: string }
    >({
      query: params => ({
        url: endpoints.EMAIL_LOGS,
        method: 'GET',
        params: {
          status: params.status || '',
          provider: params.provider || '',
          days: params.days || '7',
        },
      }),
      transformResponse: (response: any) => {
        const apiResponse = response.data || response;

        let data = apiResponse.data;

        if (apiResponse.data && apiResponse.data.data) {
          data = apiResponse.data.data;
        }

        if (!data && Array.isArray(apiResponse)) {
          data = apiResponse;
        }

        return {
          message: apiResponse.message || 'Success',
          status: apiResponse.status || 'success',
          data: data || [],
        };
      },
      providesTags: ['EmailLogs'],
    }),

    // Get email statistics
    getEmailStats: builder.query<
      EmailStatsResponse,
      { days?: string; provider?: string }
    >({
      query: params => ({
        url: endpoints.EMAIL_STATS,
        method: 'GET',
        params: {
          days: params.days || '30',
          provider: params.provider || '',
        },
      }),
      transformResponse: (response: any) => {
        const apiResponse = response.data || response;

        let data = apiResponse.data;

        if (apiResponse.data && apiResponse.data.data) {
          data = apiResponse.data.data;
        }

        if (!data && apiResponse) {
          data = apiResponse;
        }

        return {
          message: apiResponse.message || 'Success',
          status: apiResponse.status || 'success',
          data: data || {
            totalEmails: 0,
            sentEmails: 0,
            failedEmails: 0,
            deliveryRate: 0,
            openRate: 0,
            clickRate: 0,
            bounceRate: 0,
            spamRate: 0,
            dailyStats: [],
            providerStats: [],
          },
        };
      },
      providesTags: ['EmailStats'],
    }),
  }),
});

export const { actions: emailConfigActions } = slice;

export const useEmailConfigSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    // General email providers
    useGetEmailProvidersQuery: api.useLazyGetEmailProvidersQuery,
    useAddEmailProviderMutation: api.useAddEmailProviderMutation,
    useUpdateEmailProviderMutation: api.useUpdateEmailProviderMutation,
    useDeleteEmailProviderMutation: api.useDeleteEmailProviderMutation,
    useTestEmailProviderMutation: api.useTestEmailProviderMutation,
    useToggleProviderStatusMutation: api.useToggleProviderStatusMutation,

    // Brevo specific
    useGetBrevoConfigQuery: api.useLazyGetBrevoConfigQuery,
    useAddBrevoConfigMutation: api.useAddBrevoConfigMutation,
    useUpdateBrevoConfigMutation: api.useUpdateBrevoConfigMutation,
    useDeleteBrevoConfigMutation: api.useDeleteBrevoConfigMutation,
    useTestBrevoConfigMutation: api.useTestBrevoConfigMutation,
    useToggleBrevoStatusMutation: api.useToggleBrevoStatusMutation,

    // Mailgun specific
    useGetMailgunConfigQuery: api.useLazyGetMailgunConfigQuery,
    useAddMailgunConfigMutation: api.useAddMailgunConfigMutation,
    useUpdateMailgunConfigMutation: api.useUpdateMailgunConfigMutation,
    useDeleteMailgunConfigMutation: api.useDeleteMailgunConfigMutation,
    useTestMailgunConfigMutation: api.useTestMailgunConfigMutation,
    useToggleMailgunStatusMutation: api.useToggleMailgunStatusMutation,

    // Email logs and stats
    useGetEmailLogsQuery: api.useLazyGetEmailLogsQuery,
    useGetEmailStatsQuery: api.useLazyGetEmailStatsQuery,
  };
};
